import { Link } from "wouter";
import { MapPin, Phone, Mail, Clock, Linkedin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { contactFormSchema, ContactFormValues } from "@/lib/validation";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";

const defaultValues: ContactFormValues = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

const Contact = () => {
    const { toast } = useToast();
    const form = useForm<ContactFormValues>({
        resolver: zodResolver(contactFormSchema),
        defaultValues,
    });

    const mutation = useMutation({
        mutationFn: (values: ContactFormValues) => {
            return apiRequest("POST", "https://online.axentbrokers.com/ajax.aspx?method=email", values);
        },
        onSuccess: () => {
            toast({
                title: "Message sent successfully",
                description: "We'll get back to you as soon as possible.",
            });
            form.reset(defaultValues);
        },
        onError: (error) => {
            toast({
                title: "Error sending message",
                description: error.message || "Please try again later.",
                variant: "destructive",
            });
        },
    });

    const onSubmit = (values: ContactFormValues) => {

        // Check if all required fields are filled
        const missingFields: string[] = [];

        if (!values.name) missingFields.push("Name");
        if (!values.email) missingFields.push("email");
        if (!values.subject) missingFields.push("Subject");
        if (!values.message) missingFields.push("Message");

        if (missingFields.length > 0) {
            toast({
                title: "Missing Information",
                description: `Please fill in the following required field${missingFields.length > 1 ? 's' : ''}: ${missingFields.join(", ")}.`,
                variant: "destructive",
            });
            return;
        }

        mutation.mutate(values);
    };

    return (
        <>
            <section className="bg-pink-900 py-20 px-4">
                <div className="max-w-7xl mx-auto text-center text-white">
                    <h1 className="text-4xl md:text-5xl font-heading font-bold mb-6">Contact Us</h1>
                    <p className="text-xl max-w-3xl mx-auto">
                        We're here to help with all your insurance needs. Get in touch with our team today.
                    </p>
                </div>
            </section>

            <section className="py-16 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-3 gap-8">
                        <div className="lg:col-span-1">
                            <h2 className="text-2xl font-heading font-semibold text-neutral-800 mb-6">Get In Touch</h2>
                            <p className="text-lg mb-8">
                                Have a question about your policy? Want to explore your coverage options? Our team is ready to assist you.
                            </p>

                            <div className="space-y-6">
                                <div className="flex items-start">
                                    <MapPin className="h-6 w-6 text-primary mt-1 mr-4 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-medium text-lg">Our Office</h3>
                                        {/*<p>13 Wolverhampton Road<br />Codsall, Wolverhampton<br />WV8 1PT</p>*/}
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <Phone className="h-6 w-6 text-primary mt-1 mr-4 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-medium text-lg">Phone</h3>
                                        <p>01902 375559</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <Mail className="h-6 w-6 text-primary mt-1 mr-4 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-medium text-lg">Email</h3>
                                        <p>info@axentbrokers.com</p>
                                    </div>
                                </div>

                                <div className="flex items-start">
                                    <Clock className="h-6 w-6 text-primary mt-1 mr-4 flex-shrink-0" />
                                    <div>
                                        <h3 className="font-medium text-lg">Office Hours</h3>
                                        <p>Monday-Friday: 9am-5pm<br />Saturday: Closed<br />Sunday: Closed</p>
                                    </div>
                                </div>
                            </div>

                            <div className="mt-8">
                                <h3 className="font-medium text-lg mb-4">Connect With Us</h3>
                                <div className="flex space-x-4">

                                    <a href="https://www.linkedin.com/company/axent-brokers-ltd" className="text-primary hover:text-primary/80 transition-colors">
                                        <Linkedin className="h-6 w-6" />
                                    </a>

                                </div>
                            </div>
                        </div>

                        <div className="lg:col-span-2 bg-white rounded-lg shadow-md p-8">
                            <h2 className="text-2xl font-heading font-semibold text-neutral-800 mb-6">Send Us a Message</h2>

                            <Form {...form}>
                                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                                    <FormField
                                        control={form.control}
                                        name="name"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Your Name</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="John Smith" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="email"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Email Address</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="john.smith@example.com" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="subject"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Subject</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="Insurance Query" {...field} />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="message"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Message</FormLabel>
                                                <FormControl>
                                                    <Textarea
                                                        placeholder="How can we help you?"
                                                        className="min-h-[150px]"
                                                        {...field}
                                                    />
                                                </FormControl>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <div className="flex justify-center">
                                    <Button
                                        type="submit"
                                        className="bg-neutral-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
                                        disabled={mutation.isPending}
                                    >
                                        {mutation.isPending ? "Sending..." : "Send Message"}
                                        </Button>
                                    </div>
                                </form>
                            </Form>
                        </div>
                    </div>
                </div>
            </section>

            <section className="py-16 px-4 bg-axent">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl font-heading font-semibold text-neutral-800 mb-4">Quick Links</h2>
                        <p className="text-lg text-neutral-800 max-w-3xl mx-auto">
                            Find the information you need quickly and easily.
                        </p>
                    </div>

                    <div className="grid sm:grid-cols-2 lg:grid-cols-2 gap-8">
                        <Link href="/services">
                            <div className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow cursor-pointer">
                                <h3 className="text-xl font-heading font-semibold mb-2">Our Services</h3>
                                <p className="text-neutral-600 mb-4">Explore our range of insurance solutions.</p>
                                <Button variant="link" className="text-primary">Learn More</Button>
                            </div>
                        </Link>

                        <Link href="/about">
                            <div className="bg-white p-6 rounded-lg shadow-md text-center hover:shadow-lg transition-shadow cursor-pointer">
                                <h3 className="text-xl font-heading font-semibold mb-2">About Us</h3>
                                <p className="text-neutral-600 mb-4">Learn about our company history and values.</p>
                                <Button variant="link" className="text-primary">Discover</Button>
                            </div>
                        </Link>

                    </div>
                </div>
            </section>

            <section className="py-16 px-4 hidden">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-12">
                        <h2 className="text-3xl font-heading font-semibold text-neutral-800 mb-4">Find Us</h2>
                        <p className="text-lg text-neutral-800 max-w-3xl mx-auto">
                            Visit our office to speak with an insurance specialist in person.
                        </p>
                    </div>

                    <div className="h-[400px] rounded-lg overflow-hidden shadow-md">
                        <iframe
                            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d193595.15830869428!2d-74.11976397304903!3d40.69766374874431!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c24fa5d33f083b%3A0xc80b8f06e177fe62!2sNew%20York%2C%20NY!5e0!3m2!1sen!2sus!4v1633373722079!5m2!1sen!2sus"
                            width="100%"
                            height="100%"
                            style={{ border: 0 }}
                            allowFullScreen
                            loading="lazy"
                            title="Axent Brokers Office Location"
                        ></iframe>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Contact;
